import { cn } from "../lib/cn";
import { useMicrocopy } from "../lib/microcopy";
import { Card } from "./card";
import { Skeleton } from "./overlays";

/** Paragraph-shaped placeholder: `lines` bars, the last one shorter. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn("h-3.5", i === lines - 1 ? "w-3/5" : "w-full")} />
      ))}
    </div>
  );
}

export function SkeletonCard({ className, lines = 3 }: { className?: string; lines?: number }) {
  return (
    <Card className={cn("p-5", className)} aria-hidden>
      <Skeleton className="mb-4 h-5 w-2/5" />
      <SkeletonText lines={lines} />
    </Card>
  );
}

export function SkeletonTable({ rows = 6, columns = 5, className }: { rows?: number; columns?: number; className?: string }) {
  return (
    <div className={cn("surface overflow-hidden", className)} aria-hidden>
      <div className="flex gap-4 border-b border-hairline bg-sand-50/80 px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => <Skeleton key={i} className="h-3 flex-1" />)}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b border-hairline px-4 py-3.5 last:border-0">
          {Array.from({ length: columns }).map((_, c) => <Skeleton key={c} className={cn("h-3.5 flex-1", c === 0 && "max-w-24")} />)}
        </div>
      ))}
    </div>
  );
}

/** Announces a loading region to assistive technology while showing placeholders. */
export function LoadingRegion({ children, className }: { children: React.ReactNode; className?: string }) {
  const m = useMicrocopy();
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{m("loading")}</span>
      {children}
    </div>
  );
}
